// Automatic research-driven building levels.
// Existing placed buildings inherit the upgraded definition immediately; no
// demolition/rebuild and no model scaling. Visual growth is modular metadata.

import { useGame } from "../em2c-store";
import { EM2C_BUILDINGS } from "../data/em2c-buildings";
import { FACTORY_RECIPES } from "../data/productionChains";

let unsubscribe = null;
const recipeBaseTimes = new Map();

function rememberRecipeTimes(type) {
  for (const recipe of FACTORY_RECIPES[type] || []) {
    const key = `${type}:${recipe.id}`;
    if (!recipeBaseTimes.has(key)) recipeBaseTimes.set(key, Number(recipe.time || 1));
  }
}

function setRecipeLevel(type, level) {
  rememberRecipeTimes(type);
  const speedByLevel = { 1: 1, 2: 1.6, 3: 2.4 };
  const speed = speedByLevel[level] || 1;
  for (const recipe of FACTORY_RECIPES[type] || []) {
    const base = recipeBaseTimes.get(`${type}:${recipe.id}`) || Number(recipe.time || 1);
    recipe.time = Math.max(1, base / speed);
  }
}

function batteryLevel(completed) {
  let level = 1;
  if (completed.includes("eng_battery1")) level = 2;
  if (completed.includes("eng_storage2")) level = 3;
  if (completed.includes("eng_reactor1")) level = 4;
  if (completed.includes("eng_fusion1")) level = 5;
  return level;
}

function gasIndustryLevel(completed) {
  if (completed.includes("aero_sat1") || completed.includes("aero_orbital1")) return 3;
  if (completed.includes("aero_fuel1")) return 2;
  return 1;
}

export function applyAutomaticBuildingUpgrades(completed = useGame.getState().research?.completed || []) {
  const battery = EM2C_BUILDINGS.battery_storage;
  const bLevel = batteryLevel(completed);
  if (battery?.levelCapacities) {
    battery.currentLevel = bLevel;
    battery.storageCapacity = { power: battery.levelCapacities[bLevel] };
  }

  const gasLevel = gasIndustryLevel(completed);
  for (const id of ["gas_refinery", "gas_storage", "propellant_refinery", "propellant_tank_farm"]) {
    const def = EM2C_BUILDINGS[id];
    if (!def) continue;
    def.currentLevel = gasLevel;
    def.visualModuleLevel = gasLevel;
    if (def.levelCapacities?.[gasLevel]) def.storageCapacity = { ...def.levelCapacities[gasLevel] };
  }
  setRecipeLevel("gas_refinery", gasLevel);
  setRecipeLevel("propellant_refinery", gasLevel);

  const state = useGame.getState();
  const buildings = (state.buildings || []).map((building) => {
    const def = EM2C_BUILDINGS[building.type];
    const level = def?.currentLevel || 1;
    return building.systemLevel === level ? building : { ...building, systemLevel: level };
  });
  useGame.setState({ buildings });
  return { batteryLevel: bLevel, gasIndustryLevel: gasLevel };
}

export function installBuildingUpgradeIntegration() {
  if (unsubscribe) return () => {};
  let lastKey = "";
  const run = () => {
    const completed = useGame.getState().research?.completed || [];
    const key = [...completed].sort().join("|");
    if (key === lastKey) return;
    lastKey = key;
    applyAutomaticBuildingUpgrades(completed);
  };
  run();
  unsubscribe = useGame.subscribe(run);
  return () => {
    unsubscribe?.();
    unsubscribe = null;
  };
}
